import * as THREE from "three";
import { EdgeCalculatorCoefficients } from "./EdgeCalculatorCoefficients";
import { EdgeCalculatorDirections } from "./EdgeCalculatorDirections";
import { EdgeCalculatorSettings } from "./EdgeCalculatorSettings";
import { EdgeDirection } from "./EdgeDirection";
import { ArgumentInfra3DSDKError } from "../../error/ArgumentInfra3DSDKError";
import { GeoCoords } from "../../geo/GeoCoords";
import { Spatial } from "../../geo/Spatial";
import { isNotFrontViewingPerspective, isSpherical } from "../../geo/Geo";
/**
 * @class EdgeCalculator
 *
 * @classdesc Represents a class for calculating node edges.
 */
export class EdgeCalculator {
    /**
     * Create a new edge calculator instance.
     *
     * @param {EdgeCalculatorSettings} settings - Settings struct.
     * @param {EdgeCalculatorDirections} directions - Directions struct.
     * @param {EdgeCalculatorCoefficients} coefficients - Coefficients struct.
     */
    constructor(settings, directions, coefficients) {
        this._geoCoords = new GeoCoords();
        this._settings = settings != null ? settings : new EdgeCalculatorSettings();
        this._directions = directions != null ? directions : new EdgeCalculatorDirections();
        this._coefficients = coefficients != null ? coefficients : new EdgeCalculatorCoefficients();
    }
    /**
     * Returns the potential edges to destination nodes for a set
     * of nodes with respect to a source node.
     *
     * @param {Node} node - Source node.
     * @param {Array<Node>} nodes - Potential destination nodes.
     * @param {Array<string>} fallbackKeys - Keys for destination nodes that should
     * be returned even if they do not meet the criteria for a potential edge.
     * @throws {ArgumentInfra3DSDKError} If node is not full.
     */
    getPotentialEdges(node, potentialNodes, fallbackKeys) {
        if (!node.full) {
            throw new ArgumentInfra3DSDKError("Node has to be full.");
        }
        if (!node.merged) {
            return [];
        }
        const currentDirection = Spatial.viewingDirection(node.rotation);
        const currentVerticalDirection = Spatial.angleToPlane(currentDirection.toArray(), [0, 0, 1]);
        const potentialEdges = [];
        for (const potential of potentialNodes) {
            if (!potential.merged || potential.key === node.key) {
                continue;
            }
            const enu = this._geoCoords.geodeticToEnu(potential.latLon.lat, potential.latLon.lon, potential.alt, node.latLon.lat, node.latLon.lon, node.alt);
            const motion = new THREE.Vector3(enu[0], enu[1], enu[2]);
            const distance = motion.length();
            if (distance > this._settings.maxDistance && fallbackKeys.indexOf(potential.key) < 0) {
                continue;
            }
            const verticalDistance = motion.z;
            if (verticalDistance > this._settings.maxVerticalDistance && fallbackKeys.indexOf(potential.key) < 0) {
                continue;
            }
            const motionChange = Spatial.angleBetweenVector2(currentDirection.x, currentDirection.y, motion.x, motion.y);
            const verticalMotion = Spatial.angleToPlane(motion.toArray(), [0, 0, 1]);
            const direction = Spatial.viewingDirection(potential.rotation);
            const directionChange = Spatial.angleBetweenVector2(currentDirection.x, currentDirection.y, direction.x, direction.y);
            const verticalDirection = Spatial.angleToPlane(direction.toArray(), [0, 0, 1]);
            const verticalDirectionChange = verticalDirection - currentVerticalDirection;
            const rotation = Spatial.relativeRotationAngle(node.rotation, potential.rotation);
            const worldMotionAzimuth = Spatial.angleBetweenVector2(1, 0, motion.x, motion.y);
            const sameSequence = potential.sequenceKey != null && node.sequenceKey != null && potential.sequenceKey === node.sequenceKey;
            const sameProject = potential.projectKey != null && node.projectKey != null && potential.projectKey === node.projectKey;
            const sameMergeCC = (potential.mergeCC == null && node.mergeCC == null) || potential.mergeCC === node.mergeCC;
            const sameUser = potential.userKey === node.userKey;
            const potentialEdge = {
                capturedAt: potential.capturedAt,
                directionChange: directionChange,
                distance: distance,
                spherical: isSpherical(potential.cameraType),
                key: potential.key,
                motionChange: motionChange,
                rotation: rotation,
                sameMergeCC: sameMergeCC,
                sameSequence: sameSequence,
                sameProject: sameProject,
                sameUser: sameUser,
                sequenceKey: potential.sequenceKey,
                verticalDirectionChange: verticalDirectionChange,
                verticalMotion: verticalMotion,
                worldMotionAzimuth: worldMotionAzimuth,
                verticalDistance: verticalDistance,
            };
            potentialEdges.push(potentialEdge);
        }
        return potentialEdges;
    }
    /**
     * Computes the sequence edges for a node.
     *
     * @param {Node} node - Source node.
     * @throws {ArgumentInfra3DSDKError} If node is not full.
     */
    computeSequenceEdges(node, sequence) {
        if (!node.full) {
            throw new ArgumentInfra3DSDKError("Node has to be full.");
        }
        if (node.sequenceKey !== sequence.key) {
            throw new ArgumentInfra3DSDKError("Node and sequence does not correspond.");
        }
        const edges = [];
        const nextKey = sequence.findNextKey(node.key);
        if (nextKey != null) {
            edges.push({
                data: {
                    direction: EdgeDirection.Next,
                    worldMotionAzimuth: Number.NaN,
                },
                from: node.key,
                to: nextKey,
            });
        }
        const prevKey = sequence.findPrevKey(node.key);
        if (prevKey != null) {
            edges.push({
                data: {
                    direction: EdgeDirection.Prev,
                    worldMotionAzimuth: Number.NaN,
                },
                from: node.key,
                to: prevKey,
            });
        }
        return edges;
    }
    /**
     * Computes the similar edges for a node.
     *
     * @description Similar edges for perspective images
     * look roughly in the same direction and are positioned closed to the node.
     * Similar edges for spherical only target other spherical.
     *
     * @param {Node} node - Source node.
     * @param {Array<IPotentialEdge>} potentialEdges - Potential edges.
     * @throws {ArgumentInfra3DSDKError} If node is not full.
     */
    computeSimilarEdges(node, potentialEdges) {
        if (!node.full) {
            throw new ArgumentInfra3DSDKError("Node has to be full.");
        }
        const nodeSpherical = isSpherical(node.cameraType);
        const sequenceGroups = {};
        for (const potentialEdge of potentialEdges) {
            if (potentialEdge.sequenceKey == null) {
                continue;
            }
            if (potentialEdge.sameSequence) {
                continue;
            }
            if (nodeSpherical) {
                if (!potentialEdge.spherical) {
                    continue;
                }
            }
            else {
                if (!potentialEdge.spherical &&
                    Math.abs(potentialEdge.directionChange) > this._settings.similarMaxDirectionChange) {
                    continue;
                }
            }
            if (potentialEdge.distance > this._settings.similarMaxDistance) {
                continue;
            }
            if (potentialEdge.sameUser &&
                Math.abs(potentialEdge.capturedAt - node.capturedAt) < this._settings.similarMinTimeDifference) {
                continue;
            }
            if (sequenceGroups[potentialEdge.sequenceKey] == null) {
                sequenceGroups[potentialEdge.sequenceKey] = [];
            }
            sequenceGroups[potentialEdge.sequenceKey].push(potentialEdge);
        }
        const similarEdges = [];
        const calculateScore = isSpherical(node.cameraType)
            ? (potentialEdge) => {
                return potentialEdge.distance;
            }
            : (potentialEdge) => {
                return (this._coefficients.similarDistance * potentialEdge.distance +
                    this._coefficients.similarRotation * potentialEdge.rotation);
            };
        for (const sequenceKey in sequenceGroups) {
            if (!Object.prototype.hasOwnProperty.call(sequenceGroups, sequenceKey)) {
                continue;
            }
            let lowestScore = Number.MAX_VALUE;
            let similarEdge = null;
            for (const potentialEdge of sequenceGroups[sequenceKey]) {
                const score = calculateScore(potentialEdge);
                if (score < lowestScore) {
                    lowestScore = score;
                    similarEdge = potentialEdge;
                }
            }
            if (similarEdge == null) {
                continue;
            }
            similarEdges.push(similarEdge);
        }
        return similarEdges.map((potentialEdge) => {
            return {
                data: {
                    direction: EdgeDirection.Similar,
                    worldMotionAzimuth: potentialEdge.worldMotionAzimuth,
                },
                from: node.key,
                to: potentialEdge.key,
            };
        });
    }
    /**
     * Computes the step edges for a perspective node.
     *
     * @description Step edge targets can only be other perspective nodes.
     * Returns an empty array for spherical.
     *
     * @param {Node} node - Source node.
     * @param {Array<IPotentialEdge>} potentialEdges - Potential edges.
     * @param {string} prevKey - Key of previous node in sequence.
     * @param {string} prevKey - Key of next node in sequence.
     * @throws {ArgumentInfra3DSDKError} If node is not full.
     */
    computeStepEdges(node, potentialEdges, prevKey, nextKey, potentialNodes) {
        if (!node.full) {
            throw new ArgumentInfra3DSDKError("Node has to be full.");
        }
        const edges = [];
        if (isSpherical(node.cameraType)) {
            return edges;
        }
        for (const k in this._directions.steps) {
            if (!Object.prototype.hasOwnProperty.call(this._directions.steps, k)) {
                continue;
            }
            const step = this._directions.steps[k];
            let lowestScore = Number.MAX_VALUE;
            let edge = null;
            let fallback = null;
            for (const potential of potentialEdges) {
                if (potential.spherical) {
                    continue;
                }
                if (Math.abs(potential.directionChange) > this._settings.stepMaxDirectionChange) {
                    continue;
                }
                if (Math.abs(potential.verticalDistance) > this._settings.stepMaxVerticalDistance) {
                    continue;
                }
                if ((step.direction == EdgeDirection.StepRight || step.direction == EdgeDirection.StepLeft) &&
                    potential.sameSequence) {
                    continue;
                }
                const motionDifference = Math.abs(Spatial.angleDifference(step.motionChange, potential.motionChange));
                const directionMotionDifference = Spatial.angleDifference(potential.directionChange, motionDifference);
                const drift = Math.max(Math.abs(motionDifference), Math.abs(directionMotionDifference));
                let stepMaxDrift = this._settings.stepMaxDrift;
                let settings_stepPreferredDistance = this._settings.stepPreferredDistance;
                let coefficients_stepPreferredDistance = this._coefficients.stepPreferredDistance;
                if (step.direction == EdgeDirection.StepRight || step.direction == EdgeDirection.StepLeft) {
                    stepMaxDrift = this._settings.stepMaxDriftLeftRight;
                    settings_stepPreferredDistance = this._settings.stepPreferredDistanceLeftRight;
                    coefficients_stepPreferredDistance = this._coefficients.stepPreferredDistanceLeftRight;
                }
                if (Math.abs(drift) > stepMaxDrift) {
                    continue;
                }
                const potentialKey = potential.key;
                if (step.useFallback && (potentialKey === prevKey || potentialKey === nextKey)) {
                    fallback = potential;
                }
                if (potential.distance > this._settings.stepMaxDistance) {
                    continue;
                }
                const score = (coefficients_stepPreferredDistance *
                    Math.abs(potential.distance - settings_stepPreferredDistance)) /
                    this._settings.stepMaxDistance +
                    (this._coefficients.stepMotion * motionDifference) / stepMaxDrift +
                    (this._coefficients.stepRotation * potential.rotation) / this._settings.stepMaxDirectionChange +
                    this._coefficients.stepSequencePenalty * (potential.sameSequence ? 0 : 1) +
                    this._coefficients.stepProjectPenalty * (potential.sameProject ? 0 : 1) +
                    this._coefficients.stepMergeCCPenalty * (potential.sameMergeCC ? 0 : 1);
                // use this console log for debugging and finetuning
                /* if (k == "2" && (potential.key == xxx'))
                {
                    console.log({
                        key: potential.key,
                        score_distance: (coefficients_stepPreferredDistance * Math.abs(potential.distance - settings_stepPreferredDistance)) / this._settings.stepMaxDistance,
                        score_motion: (this._coefficients.stepMotion * motionDifference) / stepMaxDrift,
                        score_rotation: (this._coefficients.stepRotation * potential.rotation) / this._settings.stepMaxDirectionChange,
                        score_total: score,
                        motionDifference: motionDifference* 180/Math.PI,
                        rotation: potential.rotation * 180/Math.PI,
                        motionChange: potential.motionChange * 180/Math.PI,
                        stepMotionChange: step.motionChange* 180/Math.PI
                    });
                } */
                if (score < lowestScore) {
                    lowestScore = score;
                    edge = potential;
                }
            }
            edge = edge == null ? fallback : edge;
            if (edge != null) {
                edges.push({
                    data: {
                        direction: step.direction,
                        worldMotionAzimuth: edge.worldMotionAzimuth,
                    },
                    from: node.key,
                    to: edge.key,
                });
            }
        }
        //make sure left and right edge are not the same sequence
        const edges_left = edges.filter((x) => x.data.direction == EdgeDirection.StepLeft);
        const edges_right = edges.filter((x) => x.data.direction == EdgeDirection.StepRight);
        let reduced_edges = edges;
        if (edges_left.length > 0 && edges_right.length > 0) {
            const left_sequence_key = potentialEdges.filter((x) => x.key == edges_left[0].to)[0].sequenceKey;
            const right_sequence_key = potentialEdges.filter((x) => x.key == edges_right[0].to)[0].sequenceKey;
            if (left_sequence_key == right_sequence_key) {
                reduced_edges = edges.filter((x) => x.data.direction != EdgeDirection.StepRight);
            }
        }
        return reduced_edges;
    }
    /**
     * Computes the turn edges for a perspective node.
     *
     * @description Turn edge targets can only be other perspective images.
     * Returns an empty array for spherical.
     *
     * @param {Node} node - Source node.
     * @param {Array<IPotentialEdge>} potentialEdges - Potential edges.
     * @throws {ArgumentInfra3DSDKError} If node is not full.
     */
    computeTurnEdges(node, potentialEdges, potentialNodes) {
        if (!node.full) {
            throw new ArgumentInfra3DSDKError("Node has to be full.");
        }
        const edges = [];
        if (isNotFrontViewingPerspective(node.cameraType, node.frame.capturesystem.orientation)) {
            return edges;
        }
        for (const k in this._directions.turns) {
            if (!Object.prototype.hasOwnProperty.call(this._directions.turns, k)) {
                continue;
            }
            const turn = this._directions.turns[k];
            let lowestScore = Number.MAX_VALUE;
            let edge = null;
            for (const potential of potentialEdges) {
                if (potential.distance > this._settings.turnMaxDistance) {
                    continue;
                }
                if (Math.abs(potential.verticalDistance) > this._settings.turnMaxVerticalDistance) {
                    continue;
                }
                const rig = turn.direction !== EdgeDirection.TurnU &&
                    potential.distance < this._settings.turnMaxRigDistance &&
                    Math.abs(potential.directionChange) > this._settings.turnMinRigDirectionChange;
                const directionDifference = Spatial.angleDifference(turn.directionChange, potential.directionChange);
                let score;
                if (rig &&
                    potential.directionChange * turn.directionChange > 0 &&
                    Math.abs(potential.directionChange) < Math.abs(turn.directionChange)) {
                    score = -Math.PI / 2 + Math.abs(potential.directionChange);
                }
                else {
                    if (Math.abs(directionDifference) > this._settings.turnMaxDirectionChange) {
                        continue;
                    }
                    const motionDifference = Math.abs(turn.motionChange ? Spatial.angleDifference(turn.motionChange, potential.motionChange) : 0);
                    if (Math.abs(motionDifference) > this._settings.turnMaxMotionChange) {
                        continue;
                    }
                    score =
                        (this._coefficients.turnDistance * potential.distance) / this._settings.turnMaxDistance +
                            (this._coefficients.turnMotion * motionDifference) / Math.PI +
                            this._coefficients.turnSequencePenalty * (potential.sameSequence ? 0 : 1) +
                            this._coefficients.turnProjectPenalty * (potential.sameProject ? 0 : 1) +
                            this._coefficients.turnMergeCCPenalty * (potential.sameMergeCC ? 0 : 1);
                }
                if (score < lowestScore) {
                    lowestScore = score;
                    edge = potential;
                }
            }
            if (edge != null) {
                edges.push({
                    data: {
                        direction: turn.direction,
                        worldMotionAzimuth: edge.worldMotionAzimuth,
                    },
                    from: node.key,
                    to: edge.key,
                });
            }
        }
        return edges;
    }
    /**
     * Computes the spherical edges for a perspective node.
     *
     * @description Perspective to spherical edge targets can only be
     * spherical nodes. Returns an empty array for spherical.
     *
     * @param {Node} node - Source node.
     * @param {Array<IPotentialEdge>} potentialEdges - Potential edges.
     * @throws {ArgumentInfra3DSDKError} If node is not full.
     */
    computePerspectiveToSphericalEdges(node, potentialEdges) {
        if (!node.full) {
            throw new ArgumentInfra3DSDKError("Node has to be full.");
        }
        if (isSpherical(node.cameraType)) {
            return [];
        }
        let lowestScore = Number.MAX_VALUE;
        let edge = null;
        for (const potential of potentialEdges) {
            if (!potential.spherical) {
                continue;
            }
            const score = (this._coefficients.sphericalPreferredDistance *
                Math.abs(potential.distance - this._settings.sphericalPreferredDistance)) /
                this._settings.sphericalMaxDistance +
                (this._coefficients.sphericalMotion * Math.abs(potential.motionChange)) / Math.PI +
                this._coefficients.sphericalMergeCCPenalty * (potential.sameMergeCC ? 0 : 1);
            if (score < lowestScore) {
                lowestScore = score;
                edge = potential;
            }
        }
        if (edge == null) {
            return [];
        }
        return [
            {
                data: {
                    direction: EdgeDirection.Spherical,
                    worldMotionAzimuth: edge.worldMotionAzimuth,
                },
                from: node.key,
                to: edge.key,
            },
        ];
    }
    /**
     * Computes the spherical and step edges for a spherical node.
     *
     * @description Spherical to spherical edge targets can only be
     * spherical nodes. spherical to step edge targets can only be perspective
     * nodes.
     *
     * @param {Node} node - Source node.
     * @param {Array<IPotentialEdge>} potentialEdges - Potential edges.
     * @throws {ArgumentInfra3DSDKError} If node is not full.
     */
    computeSphericalEdges(node, potentialEdges, potentialNodes) {
        if (!node.full) {
            throw new ArgumentInfra3DSDKError("Node has to be full.");
        }
        if (!isSpherical(node.cameraType)) {
            return [];
        }
        const sphericalEdges = [];
        const potentialSpherical = [];
        const potentialSteps = [];
        for (const potential of potentialEdges) {
            if (potential.distance > this._settings.sphericalMaxDistance) {
                continue;
            }
            if (Math.abs(potential.verticalDistance) > this._settings.sphericalMaxVerticalDistance) {
                continue;
            }
            if (potential.spherical) {
                if (potential.distance < this._settings.sphericalMinDistance) {
                    continue;
                }
                potentialSpherical.push(potential);
            }
            else {
                for (const k in this._directions.spherical) {
                    if (!Object.prototype.hasOwnProperty.call(this._directions.spherical, k)) {
                        continue;
                    }
                    const spherical = this._directions.spherical[k];
                    const turn = Spatial.angleDifference(potential.directionChange, potential.motionChange);
                    const turnChange = Spatial.angleDifference(spherical.directionChange, turn);
                    if (Math.abs(turnChange) > this._settings.sphericalMaxStepTurnChange) {
                        continue;
                    }
                    potentialSteps.push([spherical.direction, potential]);
                    // break if step direction found
                    break;
                }
            }
        }
        const maxRotationDifference = Math.PI / this._settings.sphericalMaxItems;
        const occupiedAngles = [];
        const stepAngles = [];
        for (let index = 0; index < this._settings.sphericalMaxItems; index++) {
            const rotation = (index / this._settings.sphericalMaxItems) * 2 * Math.PI;
            let lowestScore = Number.MAX_VALUE;
            let edge = null;
            for (const potential of potentialSpherical) {
                const motionDifference = Spatial.angleDifference(rotation, potential.motionChange);
                if (Math.abs(motionDifference) > maxRotationDifference) {
                    continue;
                }
                let occupiedDifference = Number.MAX_VALUE;
                for (const occupiedAngle of occupiedAngles) {
                    const difference = Math.abs(Spatial.angleDifference(occupiedAngle, potential.motionChange));
                    if (difference < occupiedDifference) {
                        occupiedDifference = difference;
                    }
                }
                if (occupiedDifference <= maxRotationDifference) {
                    continue;
                }
                const score = (this._coefficients.sphericalPreferredDistance *
                    Math.abs(potential.distance - this._settings.sphericalPreferredDistance)) /
                    this._settings.sphericalMaxDistance +
                    (this._coefficients.sphericalMotion * Math.abs(motionDifference)) / maxRotationDifference +
                    this._coefficients.sphericalSequencePenalty * (potential.sameSequence ? 0 : 1) +
                    this._coefficients.sphericalProjectPenalty * (potential.sameProject ? 0 : 1) +
                    this._coefficients.sphericalMergeCCPenalty * (potential.sameMergeCC ? 0 : 1);
                if (score < lowestScore) {
                    lowestScore = score;
                    edge = potential;
                }
            }
            if (edge != null) {
                occupiedAngles.push(edge.motionChange);
                sphericalEdges.push({
                    data: {
                        direction: EdgeDirection.Spherical,
                        worldMotionAzimuth: edge.worldMotionAzimuth,
                    },
                    from: node.key,
                    to: edge.key,
                });
            }
            else {
                stepAngles.push(rotation);
            }
        }
        const occupiedStepAngles = {};
        occupiedStepAngles[EdgeDirection.Spherical] = occupiedAngles;
        occupiedStepAngles[EdgeDirection.StepForward] = [];
        occupiedStepAngles[EdgeDirection.StepLeft] = [];
        occupiedStepAngles[EdgeDirection.StepBackward] = [];
        occupiedStepAngles[EdgeDirection.StepRight] = [];
        for (const stepAngle of stepAngles) {
            const occupations = [];
            for (const k in this._directions.spherical) {
                if (!Object.prototype.hasOwnProperty.call(this._directions.spherical, k)) {
                    continue;
                }
                const spherical = this._directions.spherical[k];
                const allOccupiedAngles = occupiedStepAngles[EdgeDirection.Spherical]
                    .concat(occupiedStepAngles[spherical.direction])
                    .concat(occupiedStepAngles[spherical.prev])
                    .concat(occupiedStepAngles[spherical.next]);
                let lowestScore = Number.MAX_VALUE;
                let edge = null;
                for (const potential of potentialSteps) {
                    if (potential[0] !== spherical.direction) {
                        continue;
                    }
                    const motionChange = Spatial.angleDifference(stepAngle, potential[1].motionChange);
                    if (Math.abs(motionChange) > maxRotationDifference) {
                        continue;
                    }
                    let minOccupiedDifference = Number.MAX_VALUE;
                    for (const occupiedAngle of allOccupiedAngles) {
                        const occupiedDifference = Math.abs(Spatial.angleDifference(occupiedAngle, potential[1].motionChange));
                        if (occupiedDifference < minOccupiedDifference) {
                            minOccupiedDifference = occupiedDifference;
                        }
                    }
                    if (minOccupiedDifference <= maxRotationDifference) {
                        continue;
                    }
                    const score = (this._coefficients.sphericalPreferredDistance *
                        Math.abs(potential[1].distance - this._settings.sphericalPreferredDistance)) /
                        this._settings.sphericalMaxDistance +
                        (this._coefficients.sphericalMotion * Math.abs(motionChange)) / maxRotationDifference +
                        this._coefficients.sphericalProjectPenalty * (potential[1].sameProject ? 0 : 1) +
                        this._coefficients.sphericalMergeCCPenalty * (potential[1].sameMergeCC ? 0 : 1);
                    if (score < lowestScore) {
                        lowestScore = score;
                        edge = potential;
                    }
                }
                if (edge != null) {
                    occupations.push(edge);
                    sphericalEdges.push({
                        data: {
                            direction: edge[0],
                            worldMotionAzimuth: edge[1].worldMotionAzimuth,
                        },
                        from: node.key,
                        to: edge[1].key,
                    });
                }
            }
            for (const occupation of occupations) {
                occupiedStepAngles[occupation[0]].push(occupation[1].motionChange);
            }
        }
        return sphericalEdges;
    }
}
//# sourceMappingURL=EdgeCalculator.js.map